import React, { useEffect, useRef, useState } from 'react';
import { sanitizeHtml } from '../../utils/htmlSanitizer';

const HtmlPreview = React.forwardRef(({ htmlCode, selectedElement, onElementSelect, isDragMode, onHtmlChange, updateSource, isEditorMode = false }, ref) => {
    const internalRef = useRef(null);
    const previewRef = ref || internalRef;
    const [draggedElement, setDraggedElement] = useState(null);

    // Effect 1: Render HTML (skips when updateSource is 'editor')
    useEffect(() => {
        if (!previewRef.current || !htmlCode) return;

        // If the update came from the editor (visual changes), do NOT re-render the DOM
        // This preserves the DOM nodes and selection state
        if (updateSource === 'editor') return;

        // Sanitize HTML
        const cleanHTML = sanitizeHtml(htmlCode);

        // Avoid re-rendering if the content is already matching (prevents losing selection)
        if (previewRef.current.innerHTML === cleanHTML) return;

        previewRef.current.innerHTML = cleanHTML;
    }, [htmlCode, updateSource]);

    // Effect 2: Attach click listener (only in editor mode)
    useEffect(() => {
        if (!previewRef.current || !isEditorMode) return;

        const handleClick = (e) => {
            console.log('HtmlPreview: Click detected', e.target);
            if (isDragMode) return;

            // Prevent default link behavior
            if (e.target.tagName === 'A') {
                e.preventDefault();
            }

            e.stopPropagation();
            let target = e.target;

            // Traverse up if clicked on icon/span inside button
            if (target.tagName === 'I' || target.tagName === 'SPAN') {
                target = target.parentElement;
            }

            console.log('HtmlPreview: Resolved target', target);

            if (target === previewRef.current) {
                console.log('HtmlPreview: Clicked container, deselecting');
                onElementSelect(null);
                return;
            }

            console.log('HtmlPreview: Selecting element', target.tagName);
            onElementSelect(target);
        };

        console.log('HtmlPreview: Adding click listener');
        previewRef.current.addEventListener('click', handleClick);

        return () => {
            if (previewRef.current) {
                console.log('HtmlPreview: Removing click listener');
                previewRef.current.removeEventListener('click', handleClick);
            }
        };
    }, [isDragMode, onElementSelect, isEditorMode]);

    // Apply selection highlight
    useEffect(() => {
        if (!previewRef.current) return;

        // Remove previous highlights
        const previouslySelected = previewRef.current.querySelectorAll('.inspector-selected');
        previouslySelected.forEach(el => el.classList.remove('inspector-selected'));

        // Add highlight to selected element
        if (selectedElement && previewRef.current.contains(selectedElement)) {
            selectedElement.classList.add('inspector-selected');
        }
    }, [selectedElement]);

    // Hover effect logic (only in editor mode)
    useEffect(() => {
        if (!previewRef.current || isDragMode || !isEditorMode) return;

        const handleMouseOver = (e) => {
            e.stopPropagation();
            const target = e.target;

            // Remove previous hover classes
            const prevHovered = previewRef.current.querySelectorAll('.inspector-hovered');
            prevHovered.forEach(el => el.classList.remove('inspector-hovered'));

            // Add hover class to current target if it's not the container itself
            if (target !== previewRef.current && target !== selectedElement) {
                target.classList.add('inspector-hovered');
            }
        };

        const handleMouseOut = (e) => {
            e.stopPropagation();
            e.target.classList.remove('inspector-hovered');
        };

        previewRef.current.addEventListener('mouseover', handleMouseOver);
        previewRef.current.addEventListener('mouseout', handleMouseOut);

        return () => {
            if (previewRef.current) {
                previewRef.current.removeEventListener('mouseover', handleMouseOver);
                previewRef.current.removeEventListener('mouseout', handleMouseOut);
            }
        };
    }, [isDragMode, selectedElement, isEditorMode]);

    // Drag and drop functionality
    useEffect(() => {
        if (!previewRef.current || !isDragMode) return;

        const elements = previewRef.current.querySelectorAll('*');
        const draggableElements = Array.from(elements).filter(el =>
            ['DIV', 'SECTION', 'ARTICLE', 'IMG', 'BUTTON', 'H1', 'H2', 'H3', 'P'].includes(el.tagName)
        );

        const handleDragStart = (e) => {
            if (!isDragMode) return;
            setDraggedElement(e.target);
            e.target.classList.add('dragging');
            e.stopPropagation();
        };

        const handleDragOver = (e) => {
            if (!isDragMode) return;
            e.preventDefault();
            e.stopPropagation();
        };

        const handleDrop = (e) => {
            if (!isDragMode || !draggedElement) return;
            e.preventDefault();
            e.stopPropagation();

            const target = e.target;
            if (draggedElement.contains(target) || target === previewRef.current) return;

            // Reorder logic
            if (target.parentNode) {
                const bounding = target.getBoundingClientRect();
                const offset = bounding.y + (bounding.height / 2);

                if (e.clientY - offset > 0) {
                    target.parentNode.insertBefore(draggedElement, target.nextSibling);
                } else {
                    target.parentNode.insertBefore(draggedElement, target);
                }

                // Notify parent of HTML change
                if (onHtmlChange) {
                    onHtmlChange(previewRef.current.innerHTML);
                }
            }
        };

        const handleDragEnd = (e) => {
            e.target.classList.remove('dragging');
            setDraggedElement(null);
        };

        draggableElements.forEach(el => {
            el.setAttribute('draggable', 'true');
            el.classList.add('hover-target');
            el.addEventListener('dragstart', handleDragStart);
            el.addEventListener('dragover', handleDragOver);
            el.addEventListener('drop', handleDrop);
            el.addEventListener('dragend', handleDragEnd);
        });

        return () => {
            draggableElements.forEach(el => {
                el.setAttribute('draggable', 'false');
                el.classList.remove('hover-target');
                el.removeEventListener('dragstart', handleDragStart);
                el.removeEventListener('dragover', handleDragOver);
                el.removeEventListener('drop', handleDrop);
                el.removeEventListener('dragend', handleDragEnd);
            });
        };
    }, [isDragMode, draggedElement, onHtmlChange]);

    if (!htmlCode) {
        return (
            <div className="h-full w-full flex flex-col items-center justify-center text-gray-300 p-10 pointer-events-none gap-4">
                <svg className="w-16 h-16 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
                <span className="text-sm font-medium">Your animated collateral will render here.</span>
            </div>
        );
    }

    return (
        <div
            ref={previewRef}
            className={`w-full min-h-full ${isDragMode ? 'draggable-mode' : ''}`}
            style={{
                backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
                backgroundSize: '20px 20px',
                backgroundColor: '#f8fafc',
                color: '#000'
            }}
        />
    );
});

export default HtmlPreview;
